import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Check, Sparkles, Rocket, Crown } from "lucide-react";
import offersHero from "@/assets/offers-hero.jpg";

interface Plan {
  name: string;
  price: string;
  icon: React.ElementType;
  gradient: string;
  badge?: string;
  features: string[];
  highlight?: boolean;
}

const plans: Plan[] = [
  {
    name: "Starter",
    price: "1 500",
    icon: Sparkles,
    gradient: "from-slate-500 to-slate-700",
    features: [
      "Création d'entreprise assistée complète",
      "Étude de marché automatisée",
      "Business plan & prévisions financières",
      "Plan marketing personnalisé",
      "Dashboard Grafton IA",
      "Dossiers ACRE · BPI · Subventions",
    ],
    badge: "Actuel",
  },
  {
    name: "Growth",
    price: "2 000",
    icon: Rocket,
    gradient: "from-amber-500 to-orange-600",
    highlight: true,
    features: [
      "Toutes les fonctionnalités Starter",
      "CRM intelligent & suivi commercial",
      "Génération automatique de contenu",
      "Simulation 20 scénarios stratégiques",
      "Session atelier IA",
      "Accès réseau investisseurs Grafton",
    ],
  },
  {
    name: "Scale",
    price: "3 000",
    icon: Crown,
    gradient: "from-violet-500 to-purple-600",
    features: [
      "Toutes les fonctionnalités Growth",
      "Vision stratégique 5 ans",
      "Tunnel de vente automatisé complet",
      "Plan de levée de fonds",
      "Développement complet d'une PME",
    ],
  },
];

export default function OffersPage() {
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <header className="h-12 flex items-center border-b border-border/40 px-4">
            <SidebarTrigger />
            <h1 className="ml-3 text-sm font-semibold text-foreground">Offres</h1>
          </header>

          <main className="flex-1 overflow-y-auto">
            {/* Hero */}
            <div className="relative w-full h-56 md:h-72 overflow-hidden">
              <img
                src={offersHero}
                alt="Offres Grafton IA"
                className="w-full h-full object-cover"
                width={1920}
                height={640}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              <div className="absolute bottom-6 left-6 md:left-10">
                <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-1">
                  Grafton IA
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                  Nos Offres
                </h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">
                  Choisissez la formule adaptée à l'ambition de votre entreprise.
                </p>
              </div>
            </div>

            {/* Plans */}
            <div className="px-4 md:px-10 py-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {plans.map((plan) => (
                  <div
                    key={plan.name}
                    className={`relative rounded-2xl border p-6 flex flex-col transition-all duration-300 ${
                      plan.highlight
                        ? "border-primary/60 bg-card shadow-xl shadow-primary/10 scale-[1.02]"
                        : "border-border/40 bg-card hover:border-border/60"
                    }`}
                  >
                    {plan.badge && (
                      <span className="absolute -top-3 left-5 text-[10px] uppercase tracking-wider font-bold bg-primary text-primary-foreground px-3 py-1 rounded-full">
                        {plan.badge}
                      </span>
                    )}

                    {/* Icon & name */}
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`h-12 w-12 rounded-xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center shadow-lg`}
                      >
                        <plan.icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <span className="text-3xl font-bold text-foreground">{plan.price}€</span>
                      <span className="text-xs text-muted-foreground ml-1">HT / mois</span>
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 flex-1">
                      {plan.features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <button
                      className={`mt-6 w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                        plan.highlight
                          ? "bg-primary text-primary-foreground hover:bg-primary/90"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }`}
                    >
                      {plan.badge === "Actuel" ? "Plan actuel" : "Choisir cette offre"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
